import { useContext, useEffect, type FC } from 'react';
import ddt from '@wix/da-ddt';
import {
  ThemeOptions,
  ThemeSkin,
} from '@wix/da-react-context/pkg/ThemeContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import DaApp from '@wix/da-shared-react/pkg/DaApp';
import { RuntimeEnvironment } from '@wix/da-shared-react/pkg/types';
import type { ModalsRenderFunction } from '@wix/da-shared-react/pkg/Modals/DaModal';
import { composeModalRenderers } from '@wix/da-shared-react/pkg/Modals/helpers';
import { modalsRenderFunc as formModalsRenderFunc } from '@wix/da-dreamup/pkg/components/modals/modalsRenderFunc';
import { DreamupModals } from '../../../../constants';
import Page from '../Page';
import AnonLandingPage from '../AnonLandingPage';
import SpendConfirmationModal from '../SpendConfirmationModal';
import InfoModal from '../InfoModal';
import { mountPageLoadModalIfNeeded } from '../../../redux/dreamup/actions';
import Headerless from '@wix/da-ds/pkg/modals/dsPresets/HeaderlessModal/HeaderlessModal';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';

import s from './DreamupApp.scss';

export interface Props {
  language: string;
  environment?: RuntimeEnvironment;
}

const renderModals: ModalsRenderFunction = composeModalRenderers(
  (modalType, popModal, params) => {
    switch (modalType) {
      case DreamupModals.PROMPT_INFO:
        return (
          <InfoModal onRequestClose={popModal} deviation={params.deviation} />
        );
      case DreamupModals.SPEND_CONFIRMATION:
        return (
          <SpendConfirmationModal
            onRequestClose={popModal}
            deviation={params.deviation}
            confirmationActionType={params.confirmationActionType}
            biDataComponent={params.biDataComponent}
          />
        );
      case DreamupModals.MOBILE_MURO:
        const { MiniComponent: Mini, MobileComponent: MobileMuro } =
          useContext(MuroDepContext);
        if (!MobileMuro || !Mini) {
          ddt.error('muro', 'No MobileMuro found in context!');
          return null;
        }
        return (
          <Headerless useFixedMobileHeight isOpen={true} hasCloseButton>
            <MobileMuro isFull={true} deviation={params.deviation}></MobileMuro>
          </Headerless>
        );
      default:
        return null;
    }
  },
  formModalsRenderFunc
);

const DreamupPage: FC<{}> = () => {
  const { isLoggedIn } = useContext(SessionContext);

  const dispatchMountPageLoadModalIfNeeded = useAction(
    mountPageLoadModalIfNeeded
  );

  useEffect(() => {
    dispatchMountPageLoadModalIfNeeded();
  }, [dispatchMountPageLoadModalIfNeeded]);

  return isLoggedIn ? <Page /> : <AnonLandingPage />;
};
DreamupPage.displayName = 'DreamupPage';

const DreamupApp: FC<Props> = ({ language, environment }) => {
  return (
    <DaApp
      language={language}
      environment={environment}
      theme={ThemeOptions.DARK}
      themeSkin={ThemeSkin.NONE}
      modalsRenderFunc={renderModals}
      bodyClassName={s['root']}
      disableHeadTags
    >
      <DreamupPage />
    </DaApp>
  );
};
DreamupApp.displayName = 'DreamupApp';

export default DreamupApp;
